import { format, formatDistanceToNow } from 'date-fns';

/**
 * Formats a date string or Date object into a human-readable string that follows
 * the pattern used throughout the PostGrid UI (e.g. "Apr 20, 2024, 3:14 PM").
 *
 * The default pattern is `PPp` (see date-fns format tokens) but can be
 * overridden if a specific layout is required.
 */
export const formatDateTime = (
  date: string | Date | null | undefined,
  pattern = 'PPp',
): string => {
  if (!date) return '';
  const asDate = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(asDate.getTime())) return '';
  return format(asDate, pattern);
};

/**
 * Returns a relative time string such as "3 hours ago" or "in 2 days".
 */
export const formatRelativeTime = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return '';
  const asDate = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(asDate.getTime())) return '';
  return formatDistanceToNow(asDate, { addSuffix: true });
};

/**
 * Formats a number into USD currency (e.g. "$1,234.00").
 */
export const formatCurrencyUSD = (amount: number | string): string => {
  const numeric = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Number.isNaN(numeric)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(numeric);
};

/**
 * Formats a number into currency (alias for formatCurrencyUSD for consistency)
 */
export const formatAmount = formatCurrencyUSD;
